/**
 * Simple Key Value Store using idb / IndexedDB
 * @see https://www.npmjs.com/package/idb
 * */

import { openDB } from 'idb'

/**
 * @note Safari does not support Blobs in IndexedDB
 * https://developers.google.com/web/fundamentals/instant-and-offline/web-storage/indexeddb-best-practices
 * use ArrayBuffer for that kind of data
 */
export type DBStorageType = string | number | boolean | Record<string, unknown> | unknown[] | ArrayBuffer | Blob

export const openDatabase = <T = DBStorageType>(dbName: string) => {
  if (typeof window === 'undefined') {
    return null
  }
  const dbPromise = openDB(dbName, 1, {
    upgrade(db) {
      db.createObjectStore('keyval')
    },
    /**
     * Release the connection when someone deletes (or upgrades) this database.
     * Otherwise the long-lived connections the app holds would block the
     * logout purge's deleteDatabase indefinitely (GH #1909).
     */
    blocking(_currentVersion, _blockedVersion, event) {
      // Close synchronously: a close deferred to a later tick is too late and the delete reports `blocked`.
      const db = event.target
      if (db instanceof IDBDatabase) {
        db.close()
      }
    },
  })

  return {
    async has(key: IDBValidKey) {
      return Boolean((await dbPromise).getKey('keyval', key))
    },
    async get(key: IDBValidKey) {
      return (await dbPromise).get('keyval', key) as Promise<T>
    },
    async set(key: IDBValidKey, val: T) {
      return (await dbPromise).put('keyval', val, key)
    },
    async delete(key: IDBValidKey) {
      return (await dbPromise).delete('keyval', key)
    },
    async clear() {
      return (await dbPromise).clear('keyval')
    },
    async keys() {
      return (await dbPromise).getAllKeys('keyval')
    },
    async values() {
      return (await dbPromise).getAll('keyval') as Promise<T[]>
    },

    dbPromise,
  }
}
